import { useChatActions, useChatStatus } from '@ai-sdk-tools/store';
import { useApolloClient } from '@apollo/client';
import { useProvider } from '@newfront-insurance/react-provision';
import { useCallback, useRef, useState, useMemo } from 'react';
import type { ChatInputContextValue } from './chat-input-context';
import { useScrollToBottom } from '../../../../hooks/use-scroll-to-bottom';
import type { UIMessageMetadata, UIMessageWithMetadata } from '../../../../lib/types';
import type { SlashCommand } from '../../../../queries/chats';
import { GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS } from '../../../../queries/chats';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useNovaContext } from '../../hooks/use-chat-context';

/**
 * Hook that sets up all chat input logic and returns the context value.
 * This hook focuses on core input functionality (text input, submit).
 * Toolbar components (like ChatAttachments) contribute additional context
 * via ChatOverlayContext.additionalContext.
 *
 * @example
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <ChatAttachments /> // Sets additionalContext via ChatOverlayContext
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
export function useChatInputSetup(): ChatInputContextValue {
  const {
    sendMessage
  } = useChatActions<UIMessageWithMetadata>();
  const status = useChatStatus();
  const {
    accountUuid,
    authProvider
  } = useNovaContext();
  const {
    authState
  } = useProvider(authProvider);
  const isPublicMode = !authState.isLoggedIn;
  const apolloClient = useApolloClient();
  const {
    additionalContext,
    chatUuid,
    userUuid,
    isVisible,
    isStandaloneMode,
    notificationController,
    appType
  } = useChatOverlayContext();
  const [input, setInput] = useState('');
  const [selectedSlashCommand, setSelectedSlashCommand] = useState<SlashCommand | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const writtenChatUuidsRef = useRef<Set<string>>(new Set());
  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '92px';
    }
  }, []);
  const handleSubmit = useCallback(async (e?: React.FormEvent<HTMLFormElement>, manualInput?: string) => {
    e?.preventDefault();
    const {
      messageMetadata,
      ...payloadAdditionalContext
    } = additionalContext ?? {};
    const metadataFiles = messageMetadata && typeof messageMetadata === 'object' && 'files' in messageMetadata && Array.isArray(messageMetadata.files) ? messageMetadata.files : [];
    const inferredAttachmentFileUuids = metadataFiles.map(file => file && typeof file === 'object' && 'uuid' in file ? file.uuid : undefined).filter((uuid): uuid is string => typeof uuid === 'string');
    const explicitAttachmentFileUuids = Array.isArray(payloadAdditionalContext.attachmentFileUuids) ? payloadAdditionalContext.attachmentFileUuids.filter((uuid_0): uuid_0 is string => typeof uuid_0 === 'string') : [];
    const normalizedAttachmentFileUuids = explicitAttachmentFileUuids.length > 0 ? explicitAttachmentFileUuids : inferredAttachmentFileUuids;
    const normalizedAdditionalContext = {
      ...payloadAdditionalContext,
      ...(normalizedAttachmentFileUuids.length > 0 ? {
        attachmentFileUuids: normalizedAttachmentFileUuids
      } : {})
    };
    const resolvedContext = await resolveContextValues(normalizedAdditionalContext);
    const bodyData = {
      accountUuid,
      ...(resolvedContext && Object.keys(resolvedContext).length > 0 && {
        ...resolvedContext,
        additionalContext: resolvedContext
      })
    };
    const messageText = manualInput || input.trim();
    if (messageText) {
      // Build slash command metadata if applicable
      let slashCommandMetadata: Partial<UIMessageMetadata> = {};
      if (selectedSlashCommand) {
        const handlePattern = `/${selectedSlashCommand.handle}`;
        if (messageText.startsWith(handlePattern)) {
          const afterHandle = messageText.slice(handlePattern.length);
          // Ensure exact match with word boundary (must be followed by space or end of string)
          if (afterHandle === '' || afterHandle.startsWith(' ')) {
            const textAfterHandle = afterHandle.trim();
            const {
              parameters,
              extraText
            } = parseSlashCommandText(selectedSlashCommand, textAfterHandle);
            slashCommandMetadata = {
              slashCommandUuid: selectedSlashCommand.uuid,
              slashCommandParameters: Object.keys(parameters).length > 0 ? parameters : undefined,
              slashCommandExtraText: extraText || undefined
            };
          }
        }
      }
      const metadata: UIMessageWithMetadata['metadata'] = {
        files: [],
        ...(messageMetadata && typeof messageMetadata === 'object' ? messageMetadata : {}),
        ...slashCommandMetadata
      };
      sendMessage({
        role: 'user',
        parts: [{
          type: 'text',
          text: messageText
        }],
        metadata
      }, {
        body: bodyData
      });
      if (isPublicMode) {
        setInput('');
        resetHeight();
        return;
      }
      notificationController.registerStream(chatUuid, isVisible, isStandaloneMode);
      const currentChats = apolloClient.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: {
          userUuid,
          appType
        }
      });
      const chatUuidWithoutPrefix = chatUuid.replace('new-chat-', '');
      if (chatUuid.startsWith('new-chat-')) {
        // Check if we've already written this chat to the cache to prevent duplicates
        if (!writtenChatUuidsRef.current.has(chatUuid)) {
          const chatAlreadyExists = currentChats?.chatsList?.some(chat => chat.uuid === chatUuidWithoutPrefix);
          if (!chatAlreadyExists && !isPublicMode) {
            apolloClient.writeQuery({
              query: GET_CHATS_BY_FILTERS,
              variables: {
                userUuid,
                appType
              },
              data: {
                chatsList: [{
                  uuid: chatUuidWithoutPrefix,
                  accountUuid: accountUuid ?? null,
                  title: 'New Chat',
                  summary: 'New Chat',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  activeStreamId: Math.random().toString(36).substring(2, 15),
                  lastMessageAt: new Date().toISOString(),
                  hasUnreadMessages: false
                }, ...(currentChats?.chatsList ?? [])]
              }
            });

            // Mark this chat UUID as written to prevent duplicates
            writtenChatUuidsRef.current.add(chatUuid);
            apolloClient.writeQuery({
              query: GET_CHAT_WITH_MESSAGES,
              variables: {
                uuid: chatUuid
              },
              data: {
                getChat: {
                  account: {
                    uuid: accountUuid ?? null,
                    name: 'New Chat'
                  },
                  title: 'New Chat',
                  summary: 'New Chat',
                  uuid: chatUuid,
                  messages: [],
                  hasUnreadMessages: false,
                  createdAt: new Date().toISOString()
                }
              }
            });
          }
        }
      } else {
        apolloClient.writeQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: {
            userUuid,
            appType
          },
          data: {
            chatsList: currentChats?.chatsList?.map(chat_0 => {
              if (chat_0.uuid === chatUuidWithoutPrefix) {
                return {
                  ...chat_0,
                  activeStreamId: Math.random().toString(36).substring(2, 15)
                };
              }
              return chat_0;
            })
          }
        });
      }
      setInput('');
      setSelectedSlashCommand(null);
      resetHeight();
    }
  }, [input, additionalContext, sendMessage, accountUuid, resetHeight, chatUuid, userUuid, apolloClient, isVisible, isStandaloneMode, notificationController, appType, selectedSlashCommand]);
  const {
    isAtBottom,
    scrollToBottom
  } = useScrollToBottom();
  return useMemo(() => ({
    input,
    setInput,
    editorRef: editorRef as React.RefObject<HTMLTextAreaElement | HTMLDivElement>,
    handleSubmit,
    status,
    isAtBottom,
    scrollToBottom,
    selectedSlashCommand,
    setSelectedSlashCommand,
    isPublicMode
  }), [input, editorRef, handleSubmit, status, isAtBottom, scrollToBottom, selectedSlashCommand, isPublicMode]);
}

/**
 * Parse text after slash command handle into parameters and extra text.
 * TEXT parameters are extracted from double-quoted strings positionally.
 * Text not in quotes becomes extraText.
 *
 * Example: `"first param" "second param" extra text here`
 * -> parameters: { param1: "first param", param2: "second param" }
 * -> extraText: "extra text here"
 */
function parseSlashCommandText(command: SlashCommand, text: string): {
  parameters: Record<string, string>;
  extraText: string;
} {
  const parameters: Record<string, string> = {};
  if (!text) {
    return {
      parameters,
      extraText: ''
    };
  }

  // Get TEXT arguments in order (required first, then optional)
  const textArgs = [...(command.requiredArguments?.filter(a => a.type === 'TEXT') ?? []), ...(command.optionalArguments?.filter(a => a.type === 'TEXT') ?? [])];
  if (textArgs.length === 0) {
    // No TEXT params defined, all text is extra
    return {
      parameters,
      extraText: text
    };
  }

  // Extract quoted strings and remaining text
  // Match all double-quoted strings (non-greedy)
  const quoteRegex = /"([^"]*)"/g;
  const quotedStrings = Array.from(text.matchAll(quoteRegex), match => match[1]);

  // Remove quoted strings from text to get extra text
  const remainingText = text.replace(quoteRegex, '').trim();

  // Assign quoted strings to parameters positionally
  textArgs.forEach((arg, index) => {
    if (quotedStrings[index] !== undefined) {
      parameters[arg.name] = quotedStrings[index];
    }
  });

  // Any remaining quoted strings beyond parameter count are appended to extra text
  const extraQuotedStrings = quotedStrings.slice(textArgs.length);
  const extraText = [...extraQuotedStrings, remainingText].filter(Boolean).join(' ');
  return {
    parameters,
    extraText
  };
}

/**
 * Resolves any function values in a context object by calling them (supports async).
 * This allows components like OutlookChatPayload to store getter functions
 * (e.g. token getters) that are resolved fresh at send time instead of caching
 * values that can become stale.
 */
async function resolveContextValues(context: Record<string, unknown>): Promise<Record<string, unknown>> {
  const entries = Object.entries(context);
  const resolved = await Promise.all(entries.map(async ([key, value]) => [key, typeof value === 'function' ? await value() : value]));
  return Object.fromEntries(resolved);
}